import type {
    ActivityLog,
    Automation,
    AutomationFilterConfig,
    AutomationObject,
    AutomationRunStats,
    AutomationSteps,
    EventType,
    FieldDefinition,
    LoopPreset,
    LoopStep,
    PersonConfigObject,
    Run,
    Step,
} from "~/models/Automation";
import type { ApiResponse } from "~/types/ApiResponse";
import _ from "lodash";
import type {
    AutomationApp,
    AutomationAppBase,
    Connection,
} from "~/models/Connection";
import { v4 as uuidv4 } from "uuid";
import { useCloneDeep, useStartsWith } from "#imports";
import type { Filter } from "~/components/automation/step/Filter.vue";
import type { Delay } from "~/components/automation/Delay.vue";
import type { TaskConfig } from "~/components/automation/step/TaskDrawer.vue";

type StepUuid = string;
type PathStep = Step<{ steps: StepUuid[] }>;
type LoopStep = Step<{
    step_id: StepUuid;
}>;

export interface AutomationTriggerStep<T = unknown> {
    source: {
        id: number | null;
        type: string | null;
        name: string | null;
    };
    config: T;
}

export enum SourceType {
    MANUAL = "manual",
    CARERIX = "carerix",
    OTYS = "otys",
    SALESFORCE = "salesforce",
}

export enum SourceConnectType {
    OAUTH = "oauth",
    CREDENTIALS = "credentials",
    MANUAL = "manual",
    NONE = "none",
}

export interface SourceOAuth {
    type: SourceConnectType.OAUTH;
    urls: {
        redirect: string;
        show: string;
    };
}

export type AutomationType = Automation["type"];

export interface AutomationSetupState<TConfig = any> {
    type: AutomationType;
    app?: string | null;
    connection_id?: string | null;
    channel_id?: string | null;
    phone_number?: string | null;
    object?: string | null;
    trigger?: string | null;
    field?: string | null;
    config?: TConfig | null;
    scheduled_config?: Automation["scheduled_config"] | null;
    start_filter?: AutomationFilterConfig | null;
    start_delay?: Delay | null;
}

export interface SourceCredentialField {
    label: string;
    name: string;
    type: string;
}

export interface SourceCredentials {
    type: SourceConnectType.CREDENTIALS;
    fields: SourceCredentialField[];
    urls: {
        store: string;
        show: string;
    };
}

export interface SourceManual {
    type: SourceConnectType.MANUAL;
}

export interface SourceNone {
    type: SourceConnectType.NONE;
}

export type SourceConnect =
    | SourceOAuth
    | SourceCredentials
    | SourceNone
    | SourceManual;

export interface AutomationSourceMap {
    type: SourceType;
    label: string;
    logo: string | null;
    connect: SourceConnect;
    social: {
        id: number;
        payload: object | null;
    } | null;
    active: boolean;
    source?: {
        id: number;
        name: string;
    };
    articles?: {
        title: string;
        url: string;
        snippet: string;
    }[];
    data?: object;
}

export interface ProcessStepContext {
    uuid: string;
    event: EventType;
    description?: string;
    activity?: ActivityLog;
    created_at: string;
}

export interface ProcessContext {
    id?: number;
    state: string | "unknown";
    sub_state?: string;
    scheduled_at?: string;
    started_at?: string;
    finished_at?: string;
    continue_at?: string;
    cancelled_at?: string;
    current_step_uuid?: string;
    current_step_index?: number;
    steps: ProcessStepContext[];
    display?: {
        state: string;
        sub_state: string;
    };
}

export interface AutomationListItem<T = undefined> {
    label: string;
    value: string;
    type?: T;
    avatar?: { src: string; alt?: string };
}

export type AutomationFilterText = AutomationListItem<"text">;

export interface FieldDefinitionWithAvatar extends FieldDefinition {
    avatar: { src: string; alt?: string };
}

export interface AutomationFilterList extends AutomationListItem<"list"> {
    options: AutomationListItem;
}

type AutomationFilters = AutomationFilterText | AutomationFilterList;

export interface AiMetadataObject {
    label: string;
    app: string;
    keys: string[];
}

export interface AutomationConfig {
    apps: Record<string, AutomationApp>;
    lists: {
        delays: { label: string; value: string; can_skip_weekends: boolean }[];
        periods: { label: string; value: string }[];
        conditions: { label: string; value: string }[];
        languages: { label: string; value: string }[];
        countries: { label: string; value: string }[];
        users: {
            label: string;
            value: string;
            avatar: { src: string; alt: string };
        }[];
    };
    object: AutomationObject | null;
    step_objects: Record<string, AutomationObject>;
    custom_fields: AutomationFilters[];
    sync_person_objects: PersonConfigObject[];
    ai_metadata_objects: AiMetadataObject[];
    available_live_stats: string[];
    can_search_objects: boolean;
}

export type AutomationStartUpdate = Automation["trigger"] & {
    automation_type: Automation["type"];
    scheduled_config?: Automation["scheduled_config"];
    start_filter?: Automation["start_filter"];
    start_delay?: Automation["start_delay"];
};

const DEFAULT_RUN_STATS: AutomationRunStats = {
    general: {
        runs: {
            attempted: 0,
            pending: 0,
            scheduling: 0,
            running: 0,
            paused: 0,
            failed: 0,
            cancelled: 0,
            timeout: 0,
            finished: 0,
        },
        filter_stats: {
            event_count: 0,
            filter_passed: 0,
            filter_not_passed: 0,
        },
        steps: {
            attempted: 0,
            in_progress: 0,
            completed: 0,
            failed: 0,
            cancelled: 0,
            on_hold: 0,
            skipped: 0,
            average_duration: 0,
            min_duration: 0,
            max_duration: 0,
            responses: 0,
        },
    },
    steps: {},
};

export const useAutomationStore = defineStore("automations", () => {
    const initialized = ref<boolean>(false);
    const automation = ref<Automation>();
    const { user, hasPermissionTo } = useUser();
    const saving = ref<boolean>(false);
    const finding = ref<boolean>(false);
    const blockNavigation = ref<boolean>(false);
    const loading = ref<boolean>(true);
    const loadingConfig = ref<boolean>(false);
    const loadingConnections = ref<boolean>(false);
    const loadingFields = ref<boolean>(false);
    const api = useApi();
    const config = ref<AutomationConfig>({
        apps: {},
        lists: {
            periods: [],
            delays: [],
            conditions: [],
            languages: [],
            countries: [],
            users: [],
        },
        object: null,
        step_objects: {},
        custom_fields: [],
        sync_person_objects: [],
        ai_metadata_objects: [],
        can_search_objects: false,
        available_live_stats: [],
    });

    const dummy_fields = ref<Record<string, any>>({});

    const fields = ref<{
        apps: Record<string, AutomationAppBase>;
        fields: FieldDefinition[];
    }>({
        apps: {},
        fields: [],
    });

    const process = ref<Run>();
    const processType = ref<"process" | "event" | "none">("none");

    const {
        data: runStats,
        status: runStatsStatus,
        error: runStatsError,
        refresh: refreshRunStats,
        clear: clearRunStatsData,
    } = useAsyncData(
        "automation-run-stats",
        async () => {
            if (!automation.value?.uuid) {
                return DEFAULT_RUN_STATS;
            }

            let id = undefined;

            if (processType.value === "process" && process.value?.id) {
                id = process.value.id;
            } else if (
                processType.value === "event" &&
                process.value?.event?.id
            ) {
                id = process.value.event.id;
            }

            const { data } = await api.automations.getRunStats(
                automation.value?.uuid ?? "",
                processType.value === "process" ? "run" : processType.value,
                id,
            );

            return data;
        },
        {
            server: false,
            immediate: false,
            default: () => DEFAULT_RUN_STATS,
        },
    );

    function clearRunStats() {
        clearRunStatsData();
        runStats.value = DEFAULT_RUN_STATS;
    }

    const {
        resume: resumeRunStatsRefresh,
        pause: pauseRunStatsRefresh,
        isActive: runStatsRefreshActive,
    } = useIntervalFn(
        () => {
            if (automation.value?.uuid) {
                refreshRunStats();
            }
        },
        5000,
        {
            immediate: false,
        },
    );

    const fieldMap = computed(() => {
        return fields.value.fields.map((field: FieldDefinition) => {
            const object =
                field.object &&
                fields.value.apps[field.app] &&
                fields.value.apps[field.app].objects
                    ? (fields.value.apps[field.app].objects[field.object] ??
                      fields.value.apps[field.app].objects[
                          field.object.toLowerCase()
                      ] ??
                      fields.value.apps[field.app].objects[
                          field.object.toUpperCase()
                      ])
                    : undefined;

            let prefix = field.object ? (object ? object : field.object) : "";

            if (field.metadata?.object_label) {
                prefix = field.metadata.object_label;
            }

            let label =
                !prefix || useStartsWith(field.label, prefix)
                    ? field.label
                    : `${prefix}: ${field.label}`;

            return {
                ...field,
                label,
                avatar: {
                    alt: fields.value.apps[field.app].label,
                    src: fields.value.apps[field.app].icon,
                },
            };
        }) as FieldDefinitionWithAvatar[];
    });

    const activeStep = ref<{
        index: Automation["type"] | string | undefined;
        next: string | undefined;
        previous: string | undefined;
        container:
            | "side"
            | "overflow"
            | "screen"
            | "wider"
            | `custom:${string}`;
    }>({
        index: undefined,
        next: undefined,
        previous: undefined,
        container: "side",
    });

    const copiedStep = ref<Step | null>();

    const connections = ref<Connection[]>([]);

    const { pause, resume } = useTimeoutPoll(() => {
        if (
            automation.value &&
            useRoute().params?.id === automation.value.uuid &&
            automation.value.status === "provisioning"
        ) {
            findAutomation(automation.value.uuid, (response) => {
                if (response.data.status !== "provisioning") {
                    automation.value.status = response.data.status;
                    pause();
                }
            });
        } else {
            pause();
        }
    }, 2500);

    function setCopiedStep(step: Step) {
        copiedStep.value = {
            step: step,
            account_id: user.value?.account.id,
        };
    }

    const getCopiedStep = computed(() => {
        return copiedStep.value;
    });

    function setActiveStepIndex(
        index: number | undefined,
        container?: "side" | "overflow" | "screen",
    ) {
        if (index !== undefined) {
            activeStep.value.index = automation.value?.steps.list[index]?.uuid;
            activeStep.value.previous =
                automation.value?.steps.list[index - 1]?.uuid;
            activeStep.value.next =
                automation.value?.steps.list[index + 1]?.uuid;
        } else {
            activeStep.value.index = undefined;
            activeStep.value.previous = undefined;
            activeStep.value.next = undefined;
        }

        if (container) {
            setActiveStepContainer(container);
        }
    }

    function setActiveStepContainer(container: "side" | "overflow" | "screen") {
        activeStep.value.container = container;
    }

    async function loadConfig(id: string, background: boolean = false) {
        if (!background) {
            loadingConfig.value = true;
            initialized.value = false;
            loading.value = true;
        }

        try {
            const { data } = await api.automations.configuration(id);

            config.value = {
                ...data,
            };
        } catch (e) {
            console.error(e);
        } finally {
            loading.value = false;
            initialized.value = true;
            loadingConfig.value = false;
        }
    }

    function findAutomation(
        id: string,
        callback: (automation: ApiResponse<Automation>) => void,
    ) {
        api.automations
            .find(id, {
                query: {
                    _mode: "display",
                },
            })
            .then((response) => {
                callback(response);
            });
    }

    function fetchAutomation(
        id: string,
        background: boolean = false,
        withProcess: boolean = false,
    ) {
        if (!withProcess) {
            process.value = undefined;
        }

        if (!background) initialized.value = false;

        if (finding.value) return;

        finding.value = true;

        if (!runStatsRefreshActive.value) {
            resumeRunStatsRefresh();
        }

        findAutomation(id, (to: ApiResponse<Automation>) => {
            finding.value = false;
            automation.value = to?.data ?? undefined;

            if (to.data.status === "provisioning") {
                resume();
            }

            loadConfig(id, background);
            loadEventFields(background);
            refreshConnections(background);
            getValueCollection();

            initialized.value = true;
        });
    }

    async function initFromRun(run: Run) {
        automation.value = run.automation as Automation;
        process.value = run;
        processType.value = "process";
        dummy_fields.value = run.context?.data ?? {};
        initialized.value = true;

        if (run.state !== "finished") {
            resumeRunStatsRefresh();
        }

        try {
            finding.value = true;

            await Promise.all([
                loadConfig(automation.value.uuid),
                loadEventFields(false),
                refreshConnections(false),
            ]);
        } catch (e) {
            console.error(e);
        } finally {
            finding.value = false;
        }
    }

    async function getValueCollection() {
        if (process.value?.context?.data) {
            dummy_fields.value = process.value.context.data;
        } else {
            try {
                const { data } = await api.getValueCollection(
                    "automation",
                    automation.value?.uuid ?? "",
                );

                dummy_fields.value = data;
            } catch (e) {
                dummy_fields.value = {};
                console.error(e);
            }
        }
    }

    async function refreshConnections(background: boolean) {
        if (!background) {
            loadingConnections.value = true;
        }

        try {
            const response = await api.third_party.listRequest({
                query: {
                    pagination: "none",
                    automation_type: automation.value?.type ?? "any",
                    default_icon: true,
                    default_logo: true,
                },
            });

            connections.value = response.data;
        } catch (e) {
            console.error(e);
        } finally {
            loadingConnections.value = false;
        }
    }

    function refreshAutomation() {
        if (automation.value?.uuid) {
            findAutomation(automation.value?.uuid, (to) => {
                automation.value = to?.data ?? undefined;
            });
        }
    }

    function trackStep<T = unknown>(index: number) {
        return computed<Step<T>>({
            get: () =>
                (automation.value as Automation).steps.list[index] as Step<T>,
            set: (v: Step<T>) => {
                const steps = useCloneDeep(
                    automation.value?.steps,
                ) as Automation["steps"];
                steps.list[index] = v;
                automation.value = {
                    ...(automation.value as Automation),
                    steps,
                };
            },
        });
    }

    function createStep(): Step {
        return {
            uuid: uuidv4(),
            type: "new",
            config: null,
            next_step_uuid: null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
        };
    }

    function updateStep(
        uuid: string | "trigger",
        data: Step["config"] | AutomationStartUpdate,
        addNewStep: boolean = false,
        store: boolean = true,
    ) {
        if (!automation.value) return;

        if (["trigger", "scheduled"].includes(uuid)) {
            automation.value.trigger = {
                app: (data as Automation["trigger"])?.app ?? null,
                type: (data as Automation["trigger"])?.type ?? null,
                config: (data as Automation["trigger"])?.config ?? null,
                connection: (data as Automation["trigger"])?.connection ?? null,
                field: (data as Automation["trigger"])?.field ?? null,
                channel: (data as Automation["trigger"])?.channel ?? null,
            };

            if ((data as AutomationStartUpdate)?.scheduled_config) {
                automation.value.scheduled_config = {
                    ...automation.value.scheduled_config,
                    ...((data as AutomationStartUpdate)
                        ?.scheduled_config as Automation["scheduled_config"]),
                } as Automation["scheduled_config"];
            }

            if ((data as AutomationStartUpdate)?.automation_type) {
                automation.value.type = (
                    data as AutomationStartUpdate
                ).automation_type;
            }

            if ((data as AutomationStartUpdate)?.start_filter !== undefined) {
                automation.value.start_filter = (
                    data as AutomationStartUpdate
                ).start_filter;
            }

            if ((data as AutomationStartUpdate)?.start_delay !== undefined) {
                automation.value.start_delay = (
                    data as AutomationStartUpdate
                ).start_delay;
            }

            return save(() => {
                if (addNewStep) {
                    addStep("trigger", undefined);
                    setActiveStepIndex(0, "side");
                }
            });
        }

        const steps = useCloneDeep(
            automation.value?.steps,
        ) as Automation["steps"];
        const index = steps.list.findIndex((step) => step.uuid === uuid);

        if (steps.list[index]) {
            steps.list[index].config = data;
        }

        if (addNewStep) {
            return addStep(uuid);
        }

        saveAutomationSteps(steps, false);

        if (addNewStep) return;

        if (store) {
            saving.value = true;
            api.automations
                .steps((automation.value as Automation).uuid)
                .update(steps.list[index].uuid as string, steps.list[index])
                .then(({ data }) => {
                    steps.list[index] = data;

                    automation.value = {
                        ...(automation.value as Automation),
                        steps,
                    };
                })
                .finally(() => {
                    refreshAutomation();
                    saving.value = false;
                });
        }
    }

    function storeNewStep(
        options: {
            type: Step["type"];
            config: Step["config"];
        },
        callback?: (step: Step) => void,
    ) {
        if (
            activeStep.value.index === undefined ||
            activeStep.value.index === "trigger"
        )
            return;

        saving.value = true;

        const steps = useCloneDeep(
            automation.value?.steps,
        ) as Automation["steps"];

        const index = steps.list.findIndex(
            (step) => step.uuid === activeStep.value.index,
        );

        steps.list[index].type = options.type;
        steps.list[index].config = options.config;

        if (
            options.type === "path" &&
            options.config &&
            "steps" in (options.config as { steps: Step[] })
        ) {
            const pathSteps: Step[] = (options.config.steps as Step[]).map(
                (step) => ({
                    ...createStep(),
                    ...step,
                }),
            );

            const uuids = [];

            for (let idx = 0; idx < pathSteps.length; idx++) {
                uuids.push(pathSteps[idx].uuid);
            }

            (steps.list[index].config as any).steps = uuids;
            steps.list.push(...pathSteps);
        }

        saveAutomationSteps(steps, true, () => {
            let index = steps.list.length - 1;
            const step = automation.value?.steps.list[index] as Step;

            if (!step || !automation.value) {
                return;
            }

            if (step.type === "path") {
                index = automation.value.steps.list.findIndex(
                    ({ uuid }) => uuid === step.config.steps[0],
                );

                setActiveStepIndex(index);
                return;
            }

            if (callback) {
                callback(step);
            }
        });
    }

    function saveAutomationSteps(
        steps: Automation["steps"],
        store: boolean = true,
        callback?: () => void,
    ) {
        steps.paths = generatePaths(steps);

        automation.value = {
            ...(automation.value as Automation),
            steps: steps,
        };

        if (store) {
            save(callback);
        }
    }

    function updateNewStep(type: Step["type"], data: Step["config"]) {
        storeNewStep({ config: data, type });
    }

    function addStep(
        uuid: string,
        next?: string | "none",
        state?: Record<string, any>,
    ) {
        const index =
            uuid === "trigger"
                ? -1
                : automation.value.steps.list.findIndex(
                      (step) => step.uuid === uuid,
                  );
        const steps = useCloneDeep(
            automation.value?.steps,
        ) as Automation["steps"];
        const newStep = createStep();

        if (index >= 0) {
            newStep.next_step_uuid =
                next === "none"
                    ? null
                    : (next ?? steps.list[index].next_step_uuid);

            if (next !== "none") {
                steps.list[index].next_step_uuid = newStep.uuid as string;
            }
        } else {
            newStep.next_step_uuid =
                automation.value?.primary_step_uuid ?? null;
            automation.value = {
                ...(automation.value as Automation),
                primary_step_uuid: newStep.uuid ?? null,
            };
        }

        if (state) {
            newStep.state = state;
        }

        steps.list.splice(index + 1, 0, newStep);

        saveAutomationSteps(steps, false);

        return newStep;
    }

    function pasteCopiedStep(uuid: string) {
        api.automations
            .duplicateStep(
                automation.value?.uuid,
                copiedStep.value?.step?.uuid,
                uuid,
            )
            .then((response) => {
                storeNewStep({
                    type: response.data.type,
                    config: response.data.config,
                });
            });
    }

    function removeStep(uuid: string) {
        const steps: AutomationSteps = useCloneDeep(
            automation.value?.steps,
        ) as AutomationSteps;
        const index = steps.list.findIndex((step) => step.uuid === uuid);
        const previousIndex = steps.list.findIndex(
            (step) => step.next_step_uuid === uuid,
        );

        if (previousIndex >= 0) {
            steps.list[previousIndex].next_step_uuid =
                steps.list[index].next_step_uuid;
        }

        const deleted = steps.list.splice(index, 1);

        if (deleted[0].uuid === automation.value?.primary_step_uuid) {
            automation.value = {
                ...(automation.value as Automation),
                primary_step_uuid: deleted[0].next_step_uuid as string,
            };
        }

        steps.list = steps.list
            .map((step) => {
                if (step.type === "path") {
                    step = {
                        ...step,
                        config: {
                            ...(step.config as { steps: string[] }),
                            steps: (
                                step.config as {
                                    steps: string[];
                                }
                            ).steps.filter(
                                (uuid) =>
                                    uuid !== deleted[0].uuid &&
                                    steps.list.find(
                                        (step) => step.uuid === uuid,
                                    ),
                            ),
                        },
                    };

                    if ((step.config as { steps: string[] }).steps.length)
                        return step;

                    deleted.push(step);

                    return undefined;
                }

                if (
                    step.type === "loop" &&
                    (step as LoopStep).config.step_id === uuid
                ) {
                    step.config.step_id = steps.paths?.[uuid]?.[0];
                }

                return step;
            })
            .filter((step) => !!step) as Step[];

        saveAutomationSteps(
            steps,
            !!(deleted.length && deleted[0].uuid && deleted[0].type !== "new"),
        );
    }

    function save(callback?: () => void) {
        if (!automation.value?.uuid) return;

        saving.value = true;
        api.automations
            .update(automation.value.uuid, {
                _mode: "display",
                ...automation.value,
            })
            .then(({ data }) => {
                if (data.status === "provisioning") {
                    resume();
                }

                automation.value = data;
            })
            .finally(() => {
                if (callback) {
                    callback();
                }

                loadConfig(automation.value.uuid, true);
                loadEventFields(true);
                refreshConnections(true);
                getValueCollection();

                saving.value = false;
            });
    }

    function refresh() {
        loadConfig(automation.value.uuid, true);
        loadEventFields(true);
        refreshConnections(true);
        getValueCollection();
    }

    async function loadEventFields(background: boolean) {
        if (!automation.value?.uuid) return;
        if (!background) {
            loadingFields.value = true;
        }

        try {
            const { data } = await api.automations.fields(
                automation.value?.uuid ?? "",
                automation.value?.type ?? "any",
                process.value?.id,
            );

            fields.value = data;
        } catch (e) {
            console.error(e);
        } finally {
            loadingFields.value = false;
        }
    }

    function generatePaths(steps: Automation["steps"]) {
        function getStepMap(step: Step | undefined | string) {
            const map: string[] = [];

            while (true) {
                step = steps.list.find(
                    ({ uuid }) =>
                        uuid === ((step as Step)?.next_step_uuid ?? step),
                );

                if (step) {
                    map.push(step.uuid as string);
                } else {
                    break;
                }
            }

            return map;
        }

        const paths: Automation["steps"]["paths"] = {
            main: [
                automation.value?.primary_step_uuid as string,
                ...getStepMap(
                    steps.list.find(
                        ({ uuid }) =>
                            uuid ===
                            (automation.value?.primary_step_uuid as string),
                    ),
                ),
            ],
        };

        steps.list.forEach((step) => {
            if (step && step.type === "path") {
                for (const uuid of (step.config as { steps: string[] })
                    ?.steps ?? []) {
                    paths[uuid] = getStepMap(
                        steps.list.find((step) => step.uuid === uuid),
                    );
                }
            }

            if (step && step.type === "loop") {
                let loopStep = step as Step<LoopStep>;

                if (loopStep.config.step_id) {
                    paths[loopStep.config.step_id] = getStepMap(
                        steps.list.find(
                            (step) => step.uuid === loopStep.config.step_id,
                        ),
                    );
                }
            }
        });

        return paths;
    }

    const connection = computed(() =>
        (connections.value ?? []).find(
            (connection) =>
                connection.uuid === automation.value?.trigger.connection,
        ),
    );

    const loadingData = computed(
        () =>
            loadingConfig.value ||
            loadingFields.value ||
            loadingConnections.value,
    );

    const usedConnections = computed(() => {
        const uuids = [
            automation.value?.trigger?.connection,
            ...(automation.value?.steps?.list ?? []).map((step) => {
                return step.config?.connection;
            }),
        ];

        return (connections.value ?? []).filter((connection) =>
            uuids.includes(connection.uuid),
        );
    });

    const inactiveConnections = computed(() => {
        return usedConnections.value.filter((connection) => !connection.active);
    });

    const problems = computed(() => {
        return !!inactiveConnections.value.length;
    });

    function getUsedConnections(
        automation: Automation,
        connection_list?: Connection[],
    ) {
        const uuids = [
            automation?.trigger?.connection,
            ...(automation?.steps?.list ?? []).map((step) => {
                return step.config?.connection;
            }),
        ].filter((uuid) => !!uuid);

        return (connection_list ?? connections.value ?? []).filter(
            (connection) => uuids.includes(connection.uuid),
        );
    }

    function checkForProblems(
        automation: Automation,
        options: Partial<{ connection_list?: Connection[] }> = {},
    ) {
        const connections = getUsedConnections(
            automation,
            options.connection_list,
        );

        const inactive_connections = connections.filter(
            (connection) => !connection.active,
        );

        return {
            problems: !!inactive_connections.length,
            inactive_connections,
        };
    }

    const stepMap = computed<Step[]>(() => {
        if (
            !automation.value?.steps?.list ||
            !automation.value?.steps?.paths?.main
        )
            return [];

        const uuids: string[] = [];

        automation.value?.steps?.list
            .filter((step) => step.type === "path")
            .forEach((step) => {
                uuids.push(
                    ...((step?.config as { steps: string[] })?.steps ?? []),
                );
            });

        return automation.value?.steps?.paths.main.map((uuid) =>
            (automation.value as Automation).steps.list.find(
                (step) => step.uuid === uuid,
            ),
        );
    });

    function setProcess(
        value: Run | undefined,
        options?: { type?: "process" | "event"; withAutomation?: boolean },
    ) {
        process.value = value;
        processType.value = options?.type ?? "process";

        if (!value) {
            processType.value = "none";
        }

        if (value && options?.withAutomation) {
            automation.value = (value as Run).automation as Automation;
        }
    }

    const dayjs = useDayjs();

    const processContext = computed(() => {
        const base: ProcessContext = {
            id: process.value?.id ?? undefined,
            state: process.value?.state ?? "unknown",
            sub_state: process.value?.sub_state,
            scheduled_at: process.value?.created_at,
            started_at: process.value?.created_at,
            finished_at: process.value?.finished_at ?? undefined,
            continue_at: process.value?.continue_at ?? undefined,
            cancelled_at: process.value?.cancelled_at ?? undefined,
            current_step_uuid: process.value?.current_step?.uuid,
            current_step_index: process.value?.current_step?.uuid
                ? (automation.value?.steps?.list ?? []).findIndex(
                      (step) => step.uuid === process.value?.current_step?.uuid,
                  )
                : undefined,
            steps: [],
            display: process.value?.display,
        };

        if (!Array.isArray(process?.value?.activities)) {
            return base;
        }

        for (const activity of process?.value?.activities ?? ([] as any[])) {
            let uuid = activity?.by?.object?.uuid;
            if (activity.event === "process_scheduled") {
                base.scheduled_at = activity.created_at;
                uuid = activity.event;
            } else if (activity.event === "process_started") {
                base.started_at = activity.created_at;
                uuid = activity.event;
            } else if (activity?.by?.type !== "step") {
                uuid = activity.event;
            }

            if (activity.event === "process_cancelled") {
                base.cancelled_at = activity.created_at;
            }

            base.steps.push({
                uuid,
                event: activity.display?.event ?? activity?.event,
                description: activity.description,
                activity: activity as ActivityLog,
                created_at: activity.created_at,
            });
        }

        for (const runStep of process.value?.run_steps ?? []) {
            const step = (automation.value as Automation).steps.list.find(
                (step) => step.id === runStep.step_id,
            );

            if (!step) continue;

            base.steps.push({
                uuid: step.uuid as string,
                event: runStep.status,
                description: runStep.message,
                created_at: runStep.started_at,
                activity: {
                    id: 0,
                    log: "default",
                    event: runStep.status as unknown as EventType,
                    description: runStep.message,
                    metadata: runStep.metadata,
                    by: {
                        id: step.id,
                        type: "step",
                        object: step,
                    },
                    created_at: runStep.started_at,
                    updated_at: runStep.finished_at ?? runStep.started_at,
                    display: {
                        event: runStep.display.status,
                    },
                },
            });
        }

        return base;
    });

    function getAppObjectLabel(
        app_index: string,
        object_index: string,
    ): string | undefined {
        const app = fields.value.apps[app_index];

        if (!app || !app.objects) return undefined;

        return app.objects[object_index];
    }

    const { t, te } = useI18n();

    function conditionLabel(
        filter: Partial<Filter>,
        property: FieldDefinition | undefined,
        expectations?: boolean,
    ) {
        let key = expectations
            ? "enums.conditions.expectations"
            : "enums.conditions";

        if (
            ["datetime", "date", "time"].includes(filter.type) &&
            te(`${key}._datetime.${filter.condition}`)
        ) {
            return t(`${key}._datetime.${filter.condition}`);
        } else if (
            property &&
            ["belongs_to_many", "has_many"].includes(
                property.relationship_type,
            ) &&
            te(`${key}._to_many.${filter.condition}`)
        ) {
            return t(`${key}._to_many.${filter.condition}`);
        }

        return t(`${key}.${filter.condition}`);
    }

    function mapFilter(filter: Filter, with_expectations?: boolean, properties?: FieldDefinition[]) {
        filter = useCloneDeep(filter);

        let labels: {
            property_label: string;
            condition_label: string;
            value_label: any;
            using?: {
                icon?: string;
                label: string;
            };
        } = {
            property_label: filter.property,
            condition_label: filter.condition,
            value_label: filter.value,
        };

        const property = (properties ?? fieldMap.value).find(
            ({ key }) => key === filter.property,
        );

        const object =
            property && property.app && property.object
                ? getAppObjectLabel(property.app, property.object)
                : undefined;

        const condition = config.value.lists.conditions.find(
            ({ value }) => value === filter.condition,
        );

        labels.condition_label = conditionLabel(
            filter,
            property,
            with_expectations,
        );

        if (property) {
            labels.property_label =
                !property.object ||
                (object && property.label.startsWith(object))
                    ? property.label
                    : `${object ? object : property.object}: ${property.label}`;
        }

        if ("valid" in filter) {
            if (!filter.current_value) {
                filter.current_value = {
                    valid: -1 as unknown as boolean,
                    value: null,
                };
            }

            if (!filter.expected_value) {
                filter.expected_value = {
                    valid: -1 as unknown as boolean,
                    value: null,
                };
            }

            if (
                [null, undefined].includes(filter.current_value.value) &&
                !["is_empty", "is_not_empty"].includes(filter.condition)
            ) {
                filter.error =
                    "error" in filter ? filter.error : "Value Not Found";
            } else {
                filter.error = null;
            }
        } else {
            filter.error = null;
        }

        if ("values" in filter && typeof filter.values === "object") {
            try {
                labels.property_label =
                    ["datetime", "time", "date"].includes(filter.type) &&
                    !isNaN(new Date(filter.current_value.value))
                        ? dayjs(filter.current_value.value).calendar(null, {
                              sameElse: "DD/MM/YYYY h:mm A",
                          })
                        : filter.current_value.value;
            } catch {
                labels.property_label = filter.current_value.value;
            }

            try {
                labels.value_label =
                    ["datetime", "time", "date"].includes(filter.type) &&
                    !isNaN(new Date(filter.expected_value.value))
                        ? dayjs(filter.expected_value.value).calendar(null, {
                              sameElse: "DD/MM/YYYY h:mm A",
                          })
                        : filter.expected_value.value;
            } catch {
                labels.value_label = filter.expected_value.value;
            }
        }

        if (property) {
            if (["list", "bool"].includes(property.type)) {
                if (
                    condition &&
                    ["one_of", "not_one_of"].includes(condition.value)
                ) {
                    property.options = property.options.filter(
                        (item) => item !== null && item !== undefined,
                    );
                    const values = useArrayUnique(
                        _.uniqBy(
                            property.options.filter(({ value }) =>
                                ((filter.value as string[]) ?? []).includes(
                                    value,
                                ),
                            ),
                            "value",
                        ),
                    ).value;

                    labels.value_label =
                        values.length === 1
                            ? values[0].label
                            : [
                                  `${values.length} selected`,
                                  values.map(({ label }) => label),
                              ];
                } else {
                    const option = property.options.find(
                        (opt) => opt && filter && opt.value === filter.value,
                    );

                    labels.value_label =
                        option && option.label
                            ? option.label
                            : labels.value_label;
                }
            }
        } else if (
            condition &&
            ["one_of", "not_one_of"].includes(condition.value) &&
            Array.isArray(filter.value)
        ) {
            labels.value_label =
                filter.value.length === 1
                    ? filter.value[0]
                    : [`${filter.value.length} selected`, filter.value];
        }

        const using = filter.date_using
            ? (properties ?? fieldMap.value).find(({ key }) => key === filter.date_using)
            : undefined;

        const using_object =
            using && using.app && using.object
                ? getAppObjectLabel(using.app, using.object)
                : undefined;

        if (using) {
            labels.using = {
                icon:
                    using.app && fields.value.apps[using.app]
                        ? fields.value.apps[using.app].icon
                        : undefined,
                label:
                    !using.object ||
                    (using_object && using.label.startsWith(using_object))
                        ? using.label
                        : `${using_object ? using_object : using.object}: ${using.label}`,
            };
        }

        if (property?.options.length) {
            if (Array.isArray(filter.expected_value?.value)) {
                filter.expected_value.value = filter.expected_value?.value.map(
                    (val) => {
                        const opt = property.options.find(
                            (opt) => opt.value === val,
                        );
                        return opt ? opt.label : val;
                    },
                );
            } else if (
                filter.expected_value &&
                "value" in filter.expected_value
            ) {
                const opt = property.options.find(
                    (opt) => opt.value === filter?.expected_value?.value,
                );
                filter.expected_value.value = opt
                    ? opt.label
                    : filter.expected_value.value;
            }

            if (Array.isArray(filter.current_value?.value)) {
                filter.current_value.value = filter.current_value?.value.map(
                    (val) => {
                        const opt = property.options.find(
                            (opt) => opt.value === val,
                        );
                        return opt ? opt.label : val;
                    },
                );
            } else if (
                filter.current_value &&
                "value" in filter.current_value
            ) {
                const opt = property.options.find(
                    (opt) => opt.value === filter?.current_value?.value,
                );
                filter.current_value.value = opt
                    ? opt.label
                    : filter.current_value.value;
            }
        }

        return {
            icon:
                property && property.app && fields.value.apps[property.app]
                    ? fields.value.apps[property.app].icon
                    : undefined,
            property: labels.property_label,
            condition: labels.condition_label,
            value: labels.value_label,
            item: filter,
            ...(labels.using ? { using: labels.using } : {}),
        };
    }

    const personObjects = computed(() => {
        if (
            config.value &&
            config.value.sync_person_objects &&
            config.value.sync_person_objects.length
        ) {
            return (config.value.sync_person_objects ??
                []) as PersonConfigObject[];
        }

        return [] as PersonConfigObject[];
    });

    function buildState(): AutomationSetupState {
        const trigger = automation.value?.trigger?.type ?? "";
        const type = automation.value?.type ?? "trigger";
        return {
            type,
            app: automation.value?.trigger?.app ?? undefined,
            connection_id: automation.value?.trigger?.connection ?? undefined,
            channel_id: automation.value?.trigger?.channel ?? undefined,
            phone_number: automation.value?.trigger?.phone_number ?? undefined,
            object: trigger.split(".")[0] ?? undefined,
            trigger: trigger.split(".")[1] ?? undefined,
            field: automation.value?.trigger?.field ?? undefined,
            config: automation.value?.trigger?.config ?? {},
            scheduled_config: {
                start_date: new Date().toJSON(),
                start_timezone: undefined,
                forever: true,
                end_date: undefined,
                end_timezone: undefined,
                repeat_type: "none",
                repeat_count: 1,
                ...automation.value.scheduled_config,
            },
            start_filter: automation.value?.start_filter ?? {
                operator: "and",
                sets: [],
            },
            start_delay: automation.value?.start_delay ?? undefined,
        };
    }

    // Current State Displays
    const setupState = computed(() => {
        const state = buildState();
        const hasFilters =
            state.start_filter?.sets?.filter(
                (set) =>
                    set.filters.filter(
                        (filter) => filter.property && filter.condition,
                    ).length,
            ).length > 0;

        const withFilterLabel =
            hasFilters &&
            state.app === "hiredata" &&
            state.object === "form_response";

        let filterLabel = "";
        if (hasFilters && state.trigger === "created_or_updated") {
            state.trigger = "updated";
        }

        if (hasFilters) {
            for (const set of state.start_filter.sets) {
                for (const filter of set.filters) {
                    if (!filter.property) continue;

                    const field = fields.value.fields.find(
                        ({ key }) => key === filter.property,
                    );

                    if (field && !withFilterLabel) {
                        filterLabel = field.metadata?.object_label
                            ? `${field.metadata.object_label}: ${field.label}`
                            : field.label;
                        break;
                    }
                }
            }
        }

        const app = config.value.apps[state.app ?? ""];
        const connection = connections.value?.find(
            ({ uuid }) => uuid === state.connection_id,
        );
        const triggers = connection?.triggers ?? app?.triggers ?? [];

        const object = Array.isArray(triggers)
            ? triggers.find((trigger: any) =>
                  [trigger.object, trigger.value].includes(state.object),
              )
            : undefined;
        let triggerValue = `${state.object ?? ""}.${state.trigger ?? ""}.${state.field ?? ""}`;
        triggerValue = triggerValue.replace(/\.+$/, "");

        let trigger = Array.isArray(triggers)
            ? triggers.find((trigger: any) => trigger.value === triggerValue)
            : undefined;

        if (!trigger && object) {
            trigger = object.options?.find(
                (option: any) => option.value === triggerValue,
            );
        }

        if (!trigger && state.trigger && te(`triggers.${state.trigger}`)) {
            trigger = {
                value: `${state.object}.${state.trigger}`,
                label: t(`triggers.${state.trigger}`),
            };
        }

        let label = "";
        let title = "";
        let subtitle = "";
        if (state.type === "scheduled") {
            title = object?.label_plural ?? state.object;
        } else if (app?.custom) {
            title = t("labels.automation_trigger", {
                object: app.label,
                trigger: object?.label ?? state.object,
            });
        } else {
            let objectLabel = object?.label ?? state.object;
            let triggerLabel = trigger?.label ?? state.trigger;

            if (filterLabel) {
                triggerLabel = `${filterLabel} ${triggerLabel}`.trim();
            }

            if (!object) {
                title = triggerLabel;
            } else if (!trigger) {
                title = objectLabel;
            } else {
                title = t("labels.automation_trigger", {
                    object: objectLabel,
                    trigger: triggerLabel,
                });
            }

            if (
                state.app === "whatsapp" &&
                state.channel_id &&
                state.phone_number
            ) {
                subtitle = state.phone_number;
            }
        }

        if (state.type === "scheduled") {
            const repeats =
                state.scheduled_config &&
                state.scheduled_config.repeat_type !== "none";
            const scheduledAt = state.scheduled_config?.start_date
                ? dayjs(state.scheduled_config.start_date).calendar(null)
                : undefined;

            if (scheduledAt) {
                subtitle = scheduledAt;
            }

            if (repeats) {
                if (state.scheduled_config.repeat_count > 1) {
                    subtitle += ` (every ${state.scheduled_config.repeat_count} ${t(`enums.repeat_types.${state.scheduled_config.repeat_type}_repeat`).toLowerCase()})`;
                } else {
                    subtitle += ` (every ${t(`enums.repeat_types.${state.scheduled_config.repeat_type}`).toLowerCase()})`;
                }

                if (
                    !(
                        state.scheduled_config.forever ||
                        !state.scheduled_config.end_date
                    )
                ) {
                    subtitle += ` until ${dayjs(state.scheduled_config.end_date).calendar(null)} (${dayjs().to(state.scheduled_config.end_date)})`;
                }
            }
        }

        // From the title remove all words that are the same as the previous word
        title = title
            ?.split(" ")
            .filter((word, index, self) => {
                return self.indexOf(word) === index;
            })
            .join(" ");

        return {
            label,
            title,
            subtitle,
            hasFilters,
            state,
            app,
            connection,
            object,
            trigger,
            withFilterLabel,
        };
    });

    // Helper function to recursively collect path step UUIDs
    const collectPathStepUuids = (
        pathStepUuid: string,
        collectedIds: string[] = [],
    ): string[] => {
        if (!automation.value) return [];
        // Add the current path step UUID
        collectedIds.push(pathStepUuid);

        // Get all direct child steps of this path
        const childSteps = automation.value.steps.paths[pathStepUuid] ?? [];

        // Add all direct child steps
        collectedIds.push(...childSteps);

        // For each child step, if it's a path, recursively collect its steps
        for (const childUuid of childSteps) {
            const childStep = automation.value.steps.list.find(
                (step) => step.uuid === childUuid,
            );
            if (childStep?.type === "path") {
                for (const nestedPathUuid of childStep.config?.steps ?? []) {
                    collectPathStepUuids(nestedPathUuid, collectedIds);
                }
            }

            if (
                childStep?.type === "loop" &&
                (childStep.config as LoopStep).step_id
            ) {
                collectPathStepUuids(
                    (childStep.config as LoopStep).step_id,
                    collectedIds,
                );
            }
        }

        return collectedIds;
    };

    // Function to calculate all previous steps for a given step
    const calculatePreviousSteps = (targetStep: Step): string[] => {
        const previousIds: string[] = [];
        let foundTarget = false;

        // Process steps until we find our target
        for (const stepUuid of automation.value?.steps.paths.main ?? []) {
            const step = automation.value?.steps.list.find(
                (step) => step.uuid === stepUuid,
            );

            if (!step) continue;

            // If we found our target step, stop collecting
            if (step?.uuid === targetStep.uuid) {
                foundTarget = true;
                break;
            }

            // Add this step's UUID to previous IDs
            if (step.uuid) {
                previousIds.push(step.uuid);
            }

            // If this is a path step, collect all its nested steps
            if (step.type === "path") {
                for (const pathStepUuid of step.config?.steps ?? []) {
                    const pathIds = collectPathStepUuids(pathStepUuid);
                    previousIds.push(...pathIds);
                }
            }

            // If this is a path step, collect all its nested steps
            if (step.type === "loop" && step.config?.step_id) {
                const pathIds = collectPathStepUuids(step.config?.step_id);
                previousIds.push(...pathIds);
            }
        }

        return foundTarget ? previousIds : [];
    };

    const getPreviousSteps = (step: Step) => {
        const steps = [];
        const stepIds = calculatePreviousSteps(step);

        for (const stepId of stepIds) {
            const step = automation.value?.steps.list.find(
                (step) => step.uuid === stepId,
            );

            if (step) {
                steps.push(step);
            }
        }

        return steps;
    };

    /**
     * Recursively finds all step UUIDs that execute before a target step.
     * Traverses all paths until the target is found in a specific branch.
     * @param pathKeyToSearch The key in `automation.steps.paths` to search within.
     * @param predecessorUuids A Set to store the UUIDs of preceding steps.
     * @param targetUuid The UUID of the step to find predecessors for.
     * @returns True if the target was found down this path or its children, false otherwise.
     */
    function findPredecessorsRecursive(
        pathKeyToSearch: string,
        predecessorUuids: Set<StepUuid>,
        targetUuid: StepUuid,
    ): boolean {
        // Ensure automation data and the specific path exist
        const sequence = useCloneDeep(
            automation.value?.steps.paths[pathKeyToSearch],
        );

        if (!automation.value || !sequence) {
            return false; // Path key doesn't exist
        }

        if (pathKeyToSearch !== "main") {
            sequence.splice(0, 0, pathKeyToSearch);
        }

        let targetFoundDownstream = false;

        for (const stepUuid of sequence) {
            // If target was found earlier in this sequence or a preceding branch, stop.
            if (targetFoundDownstream) {
                break;
            }

            // Check if the current step IS the target
            if (stepUuid === targetUuid) {
                targetFoundDownstream = true;
                // Don't add the target itself, stop processing this sequence.
                break;
            }

            // Add the current step as it precedes the target (so far)
            predecessorUuids.add(stepUuid);

            // Get step details for branching
            const step = automation.value.steps.list.find(
                (s) => s.uuid === stepUuid,
            );
            if (!step) continue; // Skip if step data is missing

            // --- Loop Handling ---
            if (step.type === "loop" && (step as LoopStep).config?.step_id) {
                const loopPathKey = (step as LoopStep).config.step_id;
                // Explore inside the loop. If target is found, mark it.
                // Steps inside the loop are added directly to the main predecessor set.
                if (
                    findPredecessorsRecursive(
                        loopPathKey,
                        predecessorUuids,
                        targetUuid,
                    )
                ) {
                    targetFoundDownstream = true;
                }
            }
            // --- Path Handling (Revised) ---
            else if (step.type === "path" && (step as PathStep).config?.steps) {
                const pathBranchKeys = (step as PathStep).config.steps;
                let foundInAnyBranch = false;
                const stepsFromBranchesToAdd = new Set<StepUuid>();

                for (const branchKey of pathBranchKeys) {
                    // Create a temporary set for steps found ONLY within this branch
                    const branchPredecessors = new Set<StepUuid>();

                    // Explore this specific branch
                    const foundInThisBranch = findPredecessorsRecursive(
                        branchKey,
                        branchPredecessors, // Collect steps from this branch here
                        targetUuid,
                    );

                    // Add the steps collected from this branch to our temporary collection
                    branchPredecessors.forEach((uuid) =>
                        stepsFromBranchesToAdd.add(uuid),
                    );

                    if (foundInThisBranch) {
                        // Target found down this branch!
                        foundInAnyBranch = true;
                        // IMPORTANT: Break the loop. We don't explore other parallel branches
                        // as they are not part of the execution path to the target.
                        break;
                    }
                    // If target not found in this branch, we continue to the next branch,
                    // having already added its steps to stepsFromBranchesToAdd.
                }

                // Add all collected steps from the relevant branch(es) to the main set
                stepsFromBranchesToAdd.forEach((uuid) =>
                    predecessorUuids.add(uuid),
                );

                // If target was found in any branch, mark it for the parent path.
                if (foundInAnyBranch) {
                    targetFoundDownstream = true;
                }
            }
        }

        // Return whether the target was found anywhere in this path or its descendants
        return targetFoundDownstream;
    }

    /**
     * Gets the Set of all step UUIDs that execute before the target step UUID.
     * @param targetStepUuid The UUID of the target step.
     * @returns A Set containing the UUIDs of all preceding steps.
     */
    function getAllPrecedingStepUuids(targetStepUuid: StepUuid): Set<StepUuid> {
        const predecessorUuids = new Set<StepUuid>();
        if (!automation.value) return predecessorUuids;
        // Start traversal from 'main', collecting into the Set
        findPredecessorsRecursive("main", predecessorUuids, targetStepUuid);
        return predecessorUuids;
    }

    /**
     * Recursively searches for a target UUID within a specific path key and its descendants.
     * Used as a helper for isStepInsideLoopScope.
     * @param targetUuid The step UUID to search for.
     * @param pathKeyToSearch The key in `automation.steps.paths` to search within.
     * @returns True if the target is found within this path or nested paths/loops, false otherwise.
     */
    function searchWithinPathForKey(
        targetUuid: StepUuid,
        pathKeyToSearch: string,
    ): boolean {
        // Ensure automation data and the specific path exist
        const sequence = useCloneDeep(
            automation.value?.steps.paths[pathKeyToSearch],
        );

        if (!automation.value || !sequence) {
            return false; // Path key doesn't exist or data unavailable
        }

        sequence.splice(0, 0, pathKeyToSearch);

        for (const stepUuid of sequence) {
            // Check 1: Is the current step in the sequence the target?
            if (stepUuid === targetUuid) {
                return true; // Found it!
            }

            // Get step details for branching logic
            const step = automation.value.steps.list.find(
                (s) => s.uuid === stepUuid,
            );
            if (!step) continue; // Skip if step data is missing

            // Check 2: Recurse into nested loops within this scope
            if (step.type === "loop" && (step as LoopStep).config?.step_id) {
                const loopPathKey = (step as LoopStep).config.step_id;
                if (searchWithinPathForKey(targetUuid, loopPathKey)) {
                    return true; // Found down this nested loop path
                }
            }
            // Check 3: Recurse into nested path branches within this scope
            else if (step.type === "path" && (step as PathStep).config?.steps) {
                const pathBranchKeys = (step as PathStep).config.steps;
                for (const branchKey of pathBranchKeys) {
                    if (searchWithinPathForKey(targetUuid, branchKey)) {
                        return true; // Found down this nested path branch
                    }
                }
            }
        }

        // Target not found in this specific path sequence or its descendants
        return false;
    }

    /**
     * Checks if a target step UUID exists within the execution scope defined by a specific loop step.
     * It only searches the path associated with the loop's config.step_id and any nested structures.
     * @param targetStepUuid The UUID of the step to locate.
     * @param loopStepUuid The UUID of the loop step defining the scope.
     * @returns True if targetStepUuid is found within the loop's scope, false otherwise.
     */
    function isStepInsideLoopScope(
        targetStepUuid: StepUuid,
        loopStepUuid: StepUuid,
    ): boolean {
        if (!automation.value?.steps?.list) {
            return false; // Automation data needed
        }

        // Find the loop step object
        const loopStep = automation.value.steps.list.find(
            (s) => s.uuid === loopStepUuid,
        ) as LoopStep | undefined;

        // Validate it's a loop and has the necessary config
        if (
            !loopStep ||
            loopStep.type !== "loop" ||
            !loopStep.config?.step_id
        ) {
            console.warn(
                `Step ${loopStepUuid} is not a valid loop step with a step_id.`,
            );
            return false;
        }

        const loopPathKey = loopStep.config.step_id;

        // Start the search *only* from the loop's defined path key
        return searchWithinPathForKey(targetStepUuid, loopPathKey);
    }

    /**
     * Filters fields to include only those available at the current step.
     * Available fields have a scope active in the current execution path
     * AND do not originate from the current step itself.
     * @param allFields The complete list of field definitions.
     * @param currentStepUuid The UUID of the step where the fields are needed.
     * @returns A filtered array of available FieldDefinition objects.
     */
    function getAvailableFields<T extends FieldDefinition = FieldDefinition>(
        allFields: T[],
        currentStepUuid: string,
    ): T[] {
        const precedingUuids = getAllPrecedingStepUuids(currentStepUuid);

        automation.value?.steps.list
            .filter(
                (loopStep) =>
                    loopStep.type === "loop" &&
                    precedingUuids.has(loopStep.uuid) &&
                    (!loopStep.config?.step_id ||
                        !isStepInsideLoopScope(
                            currentStepUuid,
                            loopStep.config.step_id,
                        )),
            )
            .forEach((loopStep) => {
                if (precedingUuids.has(loopStep.uuid)) {
                    precedingUuids.delete(loopStep.uuid);
                }
            });

        const availableFields = [];

        for (const field of allFields) {
            if (field.scope_id === null || precedingUuids.has(field.scope_id)) {
                availableFields.push(field);
            }
        }

        return availableFields;
    }

    /**
     * Filters fields to include only those unavailable at the current step.
     * Unavailable fields have a scope inactive in the current execution path
     * OR originate from the current step itself.
     * @param allFields The complete list of field definitions.
     * @param currentStepUuid The UUID of the step where the fields are needed.
     * @returns A filtered array of unavailable FieldDefinition objects.
     */
    function getUnavailableFields<T extends FieldDefinition = FieldDefinition>(
        allFields: T[],
        currentStepUuid: string,
    ): T[] {
        const precedingUuids = getAllPrecedingStepUuids(currentStepUuid);

        automation.value?.steps.list
            .filter(
                (loopStep) =>
                    loopStep.type === "loop" &&
                    precedingUuids.has(loopStep.uuid) &&
                    (!loopStep.config?.step_id ||
                        !isStepInsideLoopScope(
                            currentStepUuid,
                            loopStep.config.step_id,
                        )),
            )
            .forEach((loopStep) => {
                if (precedingUuids.has(loopStep.uuid)) {
                    precedingUuids.delete(loopStep.uuid);
                }
            });

        const unavailableFields = [];

        for (const field of allFields) {
            if (
                field.scope_id !== null &&
                !precedingUuids.has(field.scope_id)
            ) {
                unavailableFields.push(field);
            }
        }

        return unavailableFields;
    }

    function getFieldInfoForStep<T extends FieldDefinition = FieldDefinition>(
        allFields: T[],
        currentStepUuid: string,
    ) {
        const available = getAvailableFields(allFields, currentStepUuid).length;

        const unavailable = getUnavailableFields(
            allFields,
            currentStepUuid,
        ).length;

        const step = automation.value?.steps?.list?.find?.(
            (step) => step.uuid === currentStepUuid,
        );

        return {
            path: getAllPrecedingStepUuids(currentStepUuid),
            step,
            fields: {
                initial: allFields.length,
                available,
                unavailable,
                difference: Math.abs(available - unavailable),
            },
        };
    }

    const loopPresets = computed(() => {
        const objects = config.value.object ? [config.value.object] : [];

        const step_objects = Object.entries(
            config.value.step_objects ?? {},
        ).map(([, value]) => value);

        objects.push(...step_objects);

        const presets: LoopPreset[] = [];

        for (const object of objects) {
            if (object.loop_presets?.length) {
                presets.push(...object.loop_presets);
            }
        }

        return presets.sort((a, b) => a.label.localeCompare(b.label));
    });

    function generateSuggestionText() {
        let suggestionData = createDottedObject({
            name: automation.value?.name,
            trigger: {
                app:
                    setupState.value?.app?.label ??
                    automation.value?.display?.app?.label,
                connection: setupState.value?.connection?.label,
                trigger:
                    setupState.value?.title ??
                    automation.value?.display?.trigger,
                subtitle: setupState.value?.subtitle,
            },
            channel: automation.value?.channel
                ? {
                      type: automation.value?.channel.type?.label,
                      status: automation.value?.channel.status?.label,
                      platform_id: automation.value?.channel.platform?.id,
                      platform_name: automation.value?.channel.platform?.name,
                  }
                : null,
            steps: {
                list: automation.value?.steps.list.map((step) => {
                    let config: TaskConfig | undefined = undefined;
                    if (step.type === "action") {
                        const actionConfig = Object.entries(
                            createDottedObject(
                                (step.config as TaskConfig<any>).config,
                            ),
                        ).filter(([key, value]) => {
                            if (!value) {
                                return false;
                            }

                            const endsWith = [
                                ".first_name",
                                ".last_name",
                                ".name",
                                ".full_name",
                                ".company",
                                ".address",
                                ".primary_phone_number",
                                ".primary_email_address",
                                ".case_goals",
                                ".url",
                                ".payload",
                                ".domain",
                            ];

                            for (const end of endsWith) {
                                if (key.endsWith(end)) {
                                    return true;
                                }
                            }

                            if (key.startsWith("utm_campaign.")) {
                                return true;
                            }

                            return false;
                        });

                        config = {
                            app: step.config.app,
                            action: step.config.action,
                            config: createUndottedObject(
                                Object.fromEntries(actionConfig),
                            ),
                        };
                    }
                    return {
                        type: step?.display?.type ?? step.type,
                        app:
                            step?.display?.app?.label ??
                            step?.config?.app ??
                            "HireData",
                        action: step?.display?.action ?? step.action,
                        channel: step.channel
                            ? {
                                  type: step.channel.type?.label,
                                  status: step.channel.status?.label,
                                  platform_id: step.channel.platform?.id,
                                  platform_name: step.channel.platform?.name,
                                  name: step.channel,
                              }
                            : null,
                        form: step.form?.name,
                        config: config,
                    };
                }),
            },
        });

        suggestionData = createUndottedObject(
            Object.fromEntries(
                Object.entries(suggestionData).filter(([_, value]) => !!value),
            ),
        );

        return JSON.stringify(suggestionData);
    }

    const {
        data: automationNameSuggestion,
        status: automationNameSuggestionStatus,
        execute: executeAutomationNameSuggestion,
    } = useAsyncData(
        "automation-name-suggestion",
        async () => {
            const input = `
                
                Please generate a name for this automation.
                The name should be max. 5-7 words and be very clear on what it does.
                Do not include any other text or instructions.
                Just generate a name that is relevant to the automation.

                This is the automation setup:
                ${generateSuggestionText()}
            `;

            const response = await api.ai_toolkit.generateRecommendations({
                preferences: input,
                json_schema: {
                    name: "automation_name",
                    description: "A sample automation name",
                    schema: {
                        type: "object",
                        properties: {
                            automation_name: {
                                type: "string",
                                description: "The automation name",
                            },
                        },
                        required: ["automation_name"],
                        additionalProperties: false,
                    },
                    strict: true,
                },
            });
            return response.data.automation_name;
        },
        {
            server: false,
            immediate: false,
        },
    );

    return {
        automation,
        config,
        connections,
        connection,
        storeNewStep,
        personObjects,
        problems,
        loadingData,
        dummy_fields,
        usedConnections,
        inactiveConnections,
        checkForProblems,
        initialized,
        activeStep,
        fetchAutomation,
        save,
        refresh,
        trackStep,
        updateStep,
        refreshConnections,
        setActiveStepIndex,
        setActiveStepContainer,
        updateNewStep,
        removeStep,
        createStep,
        saving,
        addStep,
        loading,
        fields,
        fieldMap,
        stepMap,
        loopPresets,
        loadConfig,
        conditionLabel,
        blockNavigation,
        setProcess,
        processContext,
        processActivities: computed(() => process?.value?.activities ?? []),
        process,
        processType,
        mapFilter,
        pasteCopiedStep,
        setCopiedStep,
        copiedStep,
        buildState,
        getCopiedStep,
        setupState,
        canManageTemplates: computed(() => {
            return hasPermissionTo("system.templates.manage", {
                level: "account",
                with_super_admin: false,
            });
        }),
        // Run Stats
        runStats,
        runStatsStatus,
        runStatsError,
        refreshRunStats,
        clearRunStats,
        runStatsRefreshActive,
        resumeRunStatsRefresh,
        pauseRunStatsRefresh,
        calculatePreviousSteps,
        collectPathStepUuids,
        getPreviousSteps,
        findPredecessorsRecursive,
        getAllPrecedingStepUuids,
        getAvailableFields,
        getUnavailableFields,
        getFieldInfoForStep,
        initFromRun,
        generateSuggestionText,
        automationNameSuggestion,
        automationNameSuggestionStatus,
        executeAutomationNameSuggestion,
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(
        acceptHMRUpdate(useAutomationStore, import.meta.hot),
    );
}
